'use strict';

import { find, isElement, forEach, isEmpty, debounce } from 'lodash';

import containerManager from '../../01-helpers/filter-context/container-manager';
import { publish } from '../../01-helpers/event-manager';

import { DROPDOWN_CLICKED } from '../../02-const/event-names';

/* ========================================================================== */

const moduleName = 'input-selection',
	attributeNames = {
		type: 'data-selection-type',
		value: 'data-selection-value',
		refresh: 'data-selection-refresh',
		friendlyValue: 'data-selection-friendly-value'
	},
	selectors = {
		base: `.js-${moduleName}`,
		value: `.js-${moduleName}-value`
	},
	dateFormatter = Intl.DateTimeFormat('nl', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	});

/* ========================================================================== */

function bindEvents() {
	forEach(this.inputs, (input) => {
		input.addEventListener('change', debounce((event) => {
				this._onChangeEventHandler(event)
			}, 250), true
		);
	});

	this.filterContext.onUpdate(({ updatedState }) => {
		if (!isEmpty(updatedState)) {
			this.updateTriggerLabel(updatedState);
		}
	});
}

/* ========================================================================== */

export class InputSelection {
	constructor(baseElement) {
		this._configure(baseElement);
		this._setup();
	}

	_configure(baseElement) {
		if (!isElement(baseElement)) {
			return false;
		}

		this.baseElement = baseElement;
		this.inputs = this.baseElement.querySelectorAll('input');
		this.filterContext = containerManager.getContainer();
	}

	_setup() {
		this._setInitialValue();

		bindEvents.call(this);
	}

	/* ======================================================================= */

	get key() {
		return this.baseElement.getAttribute(attributeNames.type);
	}

	get selectedInput() {
		return find(this.inputs, (input) => input.checked === true);
	}

	get selectedValue() {
		return this.key in this.filterContext.state
			? this.filterContext.state[this.key]
			: null;
	}

	get userFriendlySelectedValue() {
		if (this.key === 'date') {
			return dateFormatter.format(new Date(this.selectedValue));
		}

		const selectedInput = this.selectedInput;

		if (!selectedInput) {
			return '';
		}

		return selectedInput.hasAttribute(attributeNames.friendlyValue)
			? selectedInput.getAttribute(attributeNames.friendlyValue)
			: selectedInput.value;
	}

	/* ======================================================================= */

	select(value) {
		// We store this in a const so it doesn't have to do a look-up twice
		// when it is not currently selected.
		const selectedInput = this.selectedInput;

		// It is possible we are managing the initial state, this would mean
		// there is not currently any selected input.
		if (selectedInput && value === this.selectedValue) {
			return;
		}

		const matchingInput = find(
			this.inputs,
			(input) => input.value === value.toString()
		);

		if (!matchingInput) {
			return;
		}

		// Now we are sure that it is not currently selected and we've found
		// the new target to select, we manage the checked states. We do however
		// need to check if `selectedInput` exists, since it is possible it does
		// not when we land here initially.
		if (selectedInput) {
			selectedInput.checked = false;
		}

		matchingInput.checked = true;
	}

	_setInitialValue() {
		const key = this.key;

		// When we can find a value inside the filter context, we set this
		// as the components initial value. If we can not find any value
		// in the filter context we try to rely on a default value.
		const valueToSet =
			key in this.filterContext.state
				? this.filterContext.state[key]
				: this.baseElement.getAttribute(attributeNames.value);

		if (!valueToSet) {
			return;
		}

		this.select(valueToSet);
		this.updateTriggerLabel({
			[key]: valueToSet
		});
	}

	_onChangeEventHandler(evt) {
		// get value and update filterContext
		const element = evt.currentTarget || evt.target,
			value = element.value,
			refresh = element.getAttribute(attributeNames.refresh);

		this.filterContext.update(
			{
				[this.key]: value
			},
			{ refresh: refresh === 'true' }
		);
	}

	updateTriggerLabel(updatedState) {
		const [keyFromFilters] = Object.keys(updatedState);

		if (this.key !== keyFromFilters) {
			return;
		}

		const labelElement = this.baseElement.querySelector(selectors.value);

		if (!labelElement) {
			return;
		}

		if (labelElement.nodeName == "INPUT" || labelElement.nodeName == "TEXTAREA") {
			labelElement.value = updatedState[this.key];
		} else {
			labelElement.innerHTML = this.userFriendlySelectedValue;
		}
		
		publish(DROPDOWN_CLICKED, { dropdownId: this.baseElement.id });
	}
}

/* ========================================================================== */

const initInputSelection = () => {
	forEach(document.querySelectorAll(selectors.base), (selectionElement) => {
		new InputSelection(selectionElement);
	});
};

initInputSelection();
